/**
 * The V2 shell (V2Bureau, V2Mobile): everything starts from the conversation.
 * - Desktop: three columns. Left (300 px, panel): search and « Nouvel agent », the agents with their living characters,
 *   one thread per agent, the account button and « Relier tes apps ». Center: the conversation, or a page opened from
 *   it (with a round back button). Right (360 px, from 1200 px): the agent (AgentPanel).
 * - Phone (≤ 760 px): the agents strip on top of the conversation, the agent's sheet from the composer's agent button.
 */
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePendingApprovals } from "../lib/agent";
import { markSeen, useShellAgents, type ShellAgent } from "../lib/agents";
import { useMainMood } from "../lib/mood";
import { useMe } from "../lib/session";
import { agentSocket, useSocketStatus } from "../lib/socket";
import { AgentPanel, AgentSheet } from "./agent-panel";
import { AgentAvatarView } from "./avatar";
import { CommandPalette } from "./command-palette";
import { IconPlus, IconSearch } from "./icons";
import { cx, ROUND_BUTTON } from "./ui";

interface ShellContext {
  /** The agent whose conversation (or page) is shown. */
  agent: ShellAgent;
  agents: ShellAgent[];
  /** Opens the agent's sheet (the composer's agent button on a phone, the conversation's head below 1200 px). */
  openAgentSheet: () => void;
}

const Ctx = createContext<ShellContext | null>(null);

export function useShell(): ShellContext | null {
  return useContext(Ctx);
}

/** The conversation routes: the main agent's (« / », « /discussion ») and an extra agent's (« /agents/<id> »). */
export function conversationOf(pathname: string): string | null {
  if (pathname === "/" || pathname === "/discussion") return "main";
  const m = /^\/agents\/([^/]+)$/.exec(pathname);
  return m && m[1] !== "main" ? decodeURIComponent(m[1]!) : null;
}

export function AppShell() {
  const location = useRouterState({ select: (s) => s.location });
  const pathname = location.pathname;
  const navigate = useNavigate();
  const agents = useShellAgents();
  const conversation = conversationOf(pathname);
  const agent = agents.find((a) => a.id === (conversation ?? "main")) ?? agents[0]!;
  const [sheet, setSheet] = useState(false);
  const [searching, setSearching] = useState(false);

  // One realtime socket while the signed-in app is mounted.
  useEffect(() => {
    agentSocket.start();
    return () => agentSocket.stop();
  }, []);

  // ⌘K / Ctrl+K opens the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // « /plus » (the former « Plus » page) and deep links « ?feuille=agent » open the agent's sheet.
  const feuille = (location.search as { feuille?: unknown }).feuille;
  useEffect(() => {
    if (feuille !== "agent") return;
    if (!window.matchMedia("(min-width: 1200px)").matches) setSheet(true);
    void navigate({ to: pathname === "/discussion" ? "/discussion" : "/", search: {}, replace: true });
  }, [feuille, navigate, pathname]);

  // The conversation on screen is read.
  useEffect(() => {
    if (conversation && agent.lastAt) markSeen(agent.id, agent.lastAt);
  }, [conversation, agent.id, agent.lastAt]);

  // A new page closes the sheet.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    setSheet(false);
  }, [pathname]);

  const openAgentSheet = useCallback(() => setSheet(true), []);
  const ctx = useMemo<ShellContext>(() => ({ agent, agents, openAgentSheet }), [agent, agents, openAgentSheet]);

  return (
    <Ctx.Provider value={ctx}>
      <div className="min-h-dvh bg-bg">
        <LeftColumn agents={agents} selected={agent.id} onSearch={() => setSearching(true)} />
        {conversation ? <AgentsStrip agents={agents} selected={agent.id} /> : null}
        <RealtimeBanner />
        <main className="@container min-w-0 md:ml-[300px] wide:mr-[360px]">
          {conversation ? (
            <Outlet />
          ) : (
            // A page opened from the conversation (V2 page boards: 18 × 24 px margins).
            <div className="mx-auto w-full max-w-[1040px] px-4 pt-3.5 pb-[calc(env(safe-area-inset-bottom)+40px)] md:px-6 md:pt-[18px]">
              <Outlet />
            </div>
          )}
        </main>
        <aside aria-label={agent.name} className="fixed inset-y-0 right-0 z-20 hidden w-[360px] overflow-y-auto bg-elev [scrollbar-width:thin] wide:block">
          <AgentPanel key={agent.id} agent={agent} />
        </aside>
        <AgentSheet agent={agent} open={sheet} onClose={() => setSheet(false)} />
        <CommandPalette open={searching} onClose={() => setSearching(false)} />
      </div>
    </Ctx.Provider>
  );
}

function agentLink(a: ShellAgent): { to: "/" } | { to: "/agents/$id"; params: { id: string } } {
  return a.main ? { to: "/" } : { to: "/agents/$id", params: { id: a.id } };
}

/** The selected agent shows its live mood; a pick makes it jump with joy. */
function useTileMood(selected: boolean): ["repos" | ReturnType<typeof useMainMood>, () => void] {
  const live = useMainMood();
  const [cheer, setCheer] = useState(false);
  useEffect(() => {
    if (!cheer) return;
    const t = setTimeout(() => setCheer(false), 1000);
    return () => clearTimeout(t);
  }, [cheer]);
  return [cheer ? "joie" : selected ? live : "repos", () => setCheer(true)];
}

function AgentTile({ agent, selected, size, wide }: { agent: ShellAgent; selected: boolean; size: number; wide?: boolean }) {
  const [mood, pick] = useTileMood(selected && agent.main);
  return (
    <Link
      {...agentLink(agent)}
      aria-current={selected ? "page" : undefined}
      onClick={pick}
      className={cx(
        "flex min-w-0 flex-col items-center gap-[3px] rounded-2xl pt-2.5 pb-[7px] transition-[background,transform] duration-200 hover:-translate-y-0.5",
        wide && "w-[74px] shrink-0 pt-2 pb-1.5",
        selected ? "bg-chip" : "hover:bg-[#202020]",
      )}
    >
      <AgentAvatarView agent={agent} size={size} bare mood={mood} />
      <span className="max-w-full truncate px-1 text-[12.5px] font-medium">{agent.name}</span>
    </Link>
  );
}

function LeftColumn({ agents, selected, onSearch }: { agents: ShellAgent[]; selected: string; onSearch: () => void }) {
  const me = useMe();
  const pending = usePendingApprovals();
  const pendingCount = pending.data?.length ?? 0;
  const initial = (me?.name || me?.email || me?.phone || "?").trim().charAt(0).toUpperCase();
  return (
    <nav aria-label="Navigation principale" className="fixed inset-y-0 left-0 z-20 hidden w-[300px] flex-col gap-4 overflow-y-auto bg-elev px-3 py-3.5 [scrollbar-width:thin] md:flex">
      <div className="flex justify-end gap-2">
        <button type="button" aria-label="Rechercher" onClick={onSearch} className={ROUND_BUTTON}>
          <IconSearch width={16} height={16} strokeWidth={2.2} />
        </button>
        <Link to="/agents" aria-label="Nouvel agent" className={ROUND_BUTTON}>
          <IconPlus width={16} height={16} strokeWidth={2.4} />
        </Link>
      </div>

      <div role="group" aria-label="Tes agents" className="grid grid-cols-4 gap-0.5">
        {agents.map((a) => (
          <AgentTile key={a.id} agent={a} selected={a.id === selected} size={50} />
        ))}
      </div>

      <ul aria-label="Conversations" className="flex flex-col gap-0.5">
        {agents.map((a) => {
          const on = a.id === selected;
          return (
            <li key={a.id}>
              <Link {...agentLink(a)} className={cx("flex items-center gap-2.5 rounded-[14px] px-2.5 py-[9px] text-left transition", on ? "bg-chip" : "hover:bg-[#202020]")}>
                <AgentAvatarView agent={a} size={28} bare />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14.5px] font-semibold">{a.name}</span>
                  <span className="block truncate text-[13px] text-muted">{a.last || " "}</span>
                </span>
                {a.unread && !on ? (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-accent">
                    <span className="sr-only">Non lu</span>
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="flex-1" />
      {pendingCount > 0 ? (
        <Link to="/approbations" className="flex h-[38px] items-center justify-between rounded-full bg-chip pr-2 pl-4 text-[14px] font-semibold transition hover:bg-[#333333]">
          Validations
          <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-accent px-1.5 text-[12.5px] text-accent-fg">{pendingCount}</span>
        </Link>
      ) : null}
      <div className="flex items-center gap-2">
        <Link to="/reglages" aria-label="Réglages" className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-chip text-[14px] font-semibold transition hover:bg-[#333333]">
          {initial}
        </Link>
        <Link to="/connexions" className="flex h-[38px] flex-1 items-center justify-center rounded-full bg-chip text-[14px] font-semibold transition hover:bg-[#333333]">
          Relier tes apps
        </Link>
      </div>
    </nav>
  );
}

/** The agents strip on a phone (above the conversation), with « + » for a new agent. */
function AgentsStrip({ agents, selected }: { agents: ShellAgent[]; selected: string }) {
  return (
    <nav aria-label="Navigation principale" className="sticky top-0 z-30 flex gap-1 overflow-x-auto bg-bg px-3 pt-3.5 pb-2 [scrollbar-width:none] md:hidden">
      {agents.map((a) => (
        <AgentTile key={a.id} agent={a} selected={a.id === selected} size={44} wide />
      ))}
      <Link to="/agents" aria-label="Nouvel agent" className="flex w-[74px] shrink-0 items-center justify-center">
        <span className="flex h-[42px] w-[42px] items-center justify-center rounded-full border-[1.5px] border-dashed border-line-strong text-muted">
          <IconPlus width={16} height={16} strokeWidth={2.4} />
        </span>
      </Link>
    </nav>
  );
}

/** Shown only when the realtime connection has been down for a few seconds. */
function RealtimeBanner() {
  const status = useSocketStatus();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (status !== "closed") {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [status]);
  if (!visible) return null;
  return (
    <div className="sticky top-0 z-40 flex items-center justify-center gap-3 bg-warning-soft px-4 py-1.5 text-[12.5px] text-warning md:ml-[300px] wide:mr-[360px]" role="status">
      <span>Reconnexion…</span>
      <button type="button" className="font-medium underline underline-offset-2" onClick={() => agentSocket.reconnectNow()}>
        Réessayer
      </button>
    </div>
  );
}
